/**
 * 账户域的中文名（**唯一一份** · 表单与列表共用）
 *
 * ## 🔴 为什么抽成单独文件
 *
 * 账户表单与账户列表**都要**把 `AccountType` / `InternalCategory` / `ContraKind`
 * 显示成中文。两份各写一遍 = 同一事实两个载体（铁律 24）—— 且**必然漂移**
 * （改了表单忘了列表，§5.1.3 那三个最易混淆的品类显示成同一个词）。
 *
 * ## 🔴 哪些文案「本来就有单一源」，不要在这里再写一遍
 *
 * | 取值集 | 中文名的唯一源 | 本文件的做法 |
 * |---|---|---|
 * | `internal` **13 品类** | 🔴 **共享层内核** `INTERNAL_CATEGORY_SPEC[cat].label`（架构 §4.3） | **`internalCategorySpec()` 转发**，不在这里抄一遍 |
 * | `contra` **5 类** | 无内核 label（`enums/account.ts` 的注释即定义） | 本文件给出（纯展示） |
 * | `AccountType` **8 类** | 无内核 label | 本文件给出（纯展示） |
 *
 * ⚠️ 品类标签**刻意转发内核**而不是本文件自建表：内核的 `label` 已经被服务端用于
 * `AccountView.categoryLabel` 下发（`account.service.ts`）⇒ 端侧若再写一份，
 * **同一个品类会在"列表显示的是服务端下发值、表单显示的是端侧字面量"之间劈叉**。
 *
 * ## 边界：这是"文案"，不是"口径"
 *
 * | 判定 | 谁定 |
 * |---|---|
 * | `allowNegative`（能否欠着） | 🔴 共享层 `resolveAllowNegative()`（由品类决定，用户不可改） |
 * | 品类合法取值 | 共享层 `INTERNAL_CATEGORIES` |
 * | 中文名 | 本文件（纯展示，无判定语义） |
 */

import {
  AccountType,
  ContraKind,
  INTERNAL_CATEGORIES,
  LOAN_SUBTYPES,
  LoanSubtype,
  REPAYMENT_METHODS,
  RepaymentMethod,
  internalCategorySpec,
  type AccountType as AccountTypeValue,
  type ContraKind as ContraKindValue,
  type InternalCategory as InternalCategoryValue,
  type LoanSubtype as LoanSubtypeValue,
  type RepaymentMethod as RepaymentMethodValue,
} from '@jiaying/shared';
/**
 * 账户 8 类 → 中文名。
 *
 * 🔴 用 `Record<AccountTypeValue, string>`（**闭合**）：漏掉任一取值都是**编译错误**。
 * ⚠️ 中文名与 B 域 PRD §5.1.1 的类型表**逐字一致**（"储蓄卡"不是"借记卡"——
 * 规格用词是前者，端侧不要按自己的习惯改）。
 */
export const ACCOUNT_TYPE_LABEL: Record<AccountTypeValue, string> = {
  [AccountType.Cash]: '现金',
  [AccountType.Debit]: '储蓄卡',
  [AccountType.Ewallet]: '虚拟钱包',
  [AccountType.Credit]: '信用卡',
  [AccountType.Loan]: '贷款账户',
  [AccountType.Investment]: '投资资金账户',
  [AccountType.Internal]: '内部挂账账户',
  [AccountType.Contra]: '往来款项',
};

/** `contra` 5 类 → 中文名（`DR-022`；括号里是"这是什么钱"的提示，不是术语） */
export const CONTRA_KIND_LABEL: Record<ContraKindValue, string> = {
  [ContraKind.Receivable]: '应收（借出的钱）',
  [ContraKind.Payable]: '应付（欠的钱）',
  [ContraKind.Advance]: '垫付（代他人垫的钱）',
  [ContraKind.Reimburse]: '报销（公司 / 保险）',
  [ContraKind.Loan]: '贷款（带细分类型）',
};

/** 取账户类型中文名；未知取值**回退原值**（不猜、不空着 —— 与 `policyStatusLabel` 同精神） */
export function accountTypeLabel(type: string): string {
  return ACCOUNT_TYPE_LABEL[type as AccountTypeValue] ?? type;
}

/** 取 `contra` 事项类型中文名；未知取值回退原值 */
export function contraKindLabel(kind: string): string {
  return CONTRA_KIND_LABEL[kind as ContraKindValue] ?? kind;
}

/**
 * `contra` 事项类型**短名**（分组标题 / 卡片 chip 用 —— `AC-Q4.2` / `AC-Q4.5` · 批 `81`）。
 *
 * 🔴 **为什么需要第二张表**：`CONTRA_KIND_LABEL` 是**下拉选项**的文案（带解释括号，
 * 帮第一次建账户的人选对）；而组标题与 chip 是**扫读**用的 —— 原型里就是「应收」「应付」
 * 两个字的 tag（`15-债权债务_原型.html:300-310`）。用途不同，不能互相顶替。
 *
 * ⚠️ 但**取值集合必须与 `CONTRA_KIND_LABEL` 完全一致**（`account-labels.spec.ts` 断言
 * 两者 key 集合相等 —— 防"新增一个 `kind` 只改了一张表"）。
 */
export const CONTRA_KIND_SHORT_LABEL: Record<ContraKindValue, string> = {
  [ContraKind.Receivable]: '应收',
  [ContraKind.Payable]: '应付',
  [ContraKind.Advance]: '垫付',
  [ContraKind.Reimburse]: '报销',
  [ContraKind.Loan]: '贷款',
};

/** 取 `contra` 事项类型**短名**；未知取值回退原值（不猜、不空着） */
export function contraKindShortLabel(kind: string): string {
  return CONTRA_KIND_SHORT_LABEL[kind as ContraKindValue] ?? kind;
}

/**
 * 取 `internal` 品类中文名 —— 🔴 **转发共享层内核**（不在这里再写一张表）。
 *
 * 未知品类回退原值（内核 `internalCategorySpec()` 对未知返回 `undefined`，
 * **不静默兜底成一个默认规格** —— `account.ts` 原文）。
 */
export function internalCategoryLabel(category: string): string {
  return internalCategorySpec(category)?.label ?? category;
}

/**
 * 表单「类型」下拉的候选项（顺序 = `AccountType` 的声明顺序 = 架构 §4.2 的书写顺序）。
 *
 * ⚠️ 只在**新建**时用（`type` 不可改 —— 契约层 `UpdateAccountRequest` 就没有它）。
 */
export const ACCOUNT_TYPE_OPTIONS: readonly { value: AccountTypeValue; label: string }[] = (
  Object.values(AccountType) as AccountTypeValue[]
).map((t) => ({ value: t, label: ACCOUNT_TYPE_LABEL[t] }));

/** 表单「`internal` 品类」下拉的候选项（顺序 = 内核 `INTERNAL_CATEGORIES`） */
export const INTERNAL_CATEGORY_OPTIONS: readonly { value: InternalCategoryValue; label: string }[] =
  INTERNAL_CATEGORIES.map((c) => ({ value: c, label: internalCategoryLabel(c) }));

/** 表单「`contra` 事项类型」下拉的候选项 */
export const CONTRA_KIND_OPTIONS: readonly { value: ContraKindValue; label: string }[] = (
  Object.values(ContraKind) as ContraKindValue[]
).map((k) => ({ value: k, label: CONTRA_KIND_LABEL[k] }));

// ─────────────── 🆕 贷款参数两组标签（`DR-044`） ───────────────

/**
 * 还款方式 2 种 → 中文名（`DR-044` D4）。
 *
 * 🔴 `Record<..., string>` **闭合**：枚举加了值而这里没跟 ⇒ **编译错误**
 * （这正是"文案不许漂移"的机器保证）。
 */
export const REPAYMENT_METHOD_LABEL: Record<RepaymentMethodValue, string> = {
  [RepaymentMethod.EqualInstallment]: '等额本息（每期还款额相同）',
  [RepaymentMethod.EqualPrincipal]: '等额本金（每期本金相同）',
};

/** 贷款子类型 4 种 → 中文名（`DR-044` D5） */
export const LOAN_SUBTYPE_LABEL: Record<LoanSubtypeValue, string> = {
  [LoanSubtype.Mortgage]: '房贷',
  [LoanSubtype.Auto]: '车贷',
  [LoanSubtype.Consumer]: '消费贷 / 网贷',
  [LoanSubtype.Other]: '其他',
};

/** 取还款方式中文名；未知取值回退原值（与 `contraKindLabel` 同精神） */
export function repaymentMethodLabel(method: string): string {
  return REPAYMENT_METHOD_LABEL[method as RepaymentMethodValue] ?? method;
}

/** 取贷款子类型中文名；未知取值回退原值 */
export function loanSubtypeLabel(subtype: string): string {
  return LOAN_SUBTYPE_LABEL[subtype as LoanSubtypeValue] ?? subtype;
}

/** 表单「还款方式」下拉的候选项（顺序 = `REPAYMENT_METHODS` = 由常用到少用） */
export const REPAYMENT_METHOD_OPTIONS: readonly { value: RepaymentMethodValue; label: string }[] =
  REPAYMENT_METHODS.map((m) => ({ value: m, label: REPAYMENT_METHOD_LABEL[m] }));

/** 表单「贷款子类型」下拉的候选项（顺序 = `LOAN_SUBTYPES`） */
export const LOAN_SUBTYPE_OPTIONS: readonly { value: LoanSubtypeValue; label: string }[] =
  LOAN_SUBTYPES.map((s) => ({ value: s, label: LOAN_SUBTYPE_LABEL[s] }));

/**
 * 信用卡**余额档位**的中文名（`CreditStanding` · B 域 PRD §5.2.3）。
 *
 * 🔴 文案的唯一处：`AC-B2.6` 的红线是「**「应还」绝不用支出绿**」——
 * 颜色由档位决定（`ui/CreditCardSection` 按档位取 `notice` / `alert` 令牌），
 * 而"档位叫什么"只在这里写一次，避免两个地方各叫一个名（"待还 / 应还 / 未还"混用）。
 */
export const CREDIT_STANDING_LABEL: Record<string, string> = {
  clear: '已结清',
  overpaid: '溢缴款',
  due: '本期应还',
  dueSoon: '临近还款日',
  overdue: '已过还款日',
};

/** 未知取值 ⇒ 原样返回（不吞、不编造一个中文名） */
export function creditStandingLabel(standing: string): string {
  return CREDIT_STANDING_LABEL[standing] ?? standing;
}

/** 使用率档位的中文名（`CreditUsageLevel`；`normal` 不显示标签，故不在表里） */
export const CREDIT_USAGE_LABEL: Record<string, string> = {
  notice: '额度偏紧',
  over: '额度告急',
};

export function creditUsageLabel(level: string): string {
  return CREDIT_USAGE_LABEL[level] ?? level;
}
